import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatDressCareValue } from '../../utils/dressCare';
import './Salon.css';

const DRESS_CARE_FIELDS = new Set([
  'care_washing',
  'care_bleaching',
  'care_drying',
  'care_ironing',
  'care_dry_cleaning'
]);

const ACCESSORY_REFERENCE_FIELDS = new Set([
  'handbag_id',
  'coat_id',
  'wrap_id',
  'bolero_id',
  'tiara_id',
  'sash_id',
  'fur_jacket_id',
  'veil_id'
]);

const getReferenceFieldConfig = (fieldName) => {
  if (fieldName === 'dress_id') {
    return { cachePrefix: 'dress', resource: 'dress' };
  }
  if (ACCESSORY_REFERENCE_FIELDS.has(fieldName)) {
    return { cachePrefix: 'accessory', resource: 'accessory' };
  }
  return null;
};

const getReferenceCacheKey = (fieldName, value) => {
  if (!value && value !== 0) return null;
  const config = getReferenceFieldConfig(fieldName);
  if (!config) return null;
  const numericValue = parseInt(value, 10);
  if (Number.isNaN(numericValue)) return null;
  return `${config.cachePrefix}:${numericValue}`;
};

const ChangeLog = ({ 
  salonId, 
  entityType, 
  entityId, 
  getLocalizedFieldName,
  isVisible,
  onToggle,
  scrollContainerRef,
  titleKey = 'reservations.changeLogTitle',
  namespace = 'reservations'
}) => {
  const { t, formatWeddingDate } = useLanguage();
  const [changeLogs, setChangeLogs] = useState([]);
  const [changeLogsLoading, setChangeLogsLoading] = useState(false);
  const [changeLogsError, setChangeLogsError] = useState('');
  const [valueLabelCache, setValueLabelCache] = useState({});
  const changeLogSectionRef = useRef(null);
  const fetchedKeyRef = useRef(null);

  const fetchChangeLogs = useCallback(async () => {
    if (!entityId) return;

    setChangeLogsLoading(true);
    setChangeLogsError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/salons/${salonId}/change-logs?entityType=${entityType}&entityId=${entityId}&limit=100`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setChangeLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching change logs:', error);
      setChangeLogsError(t(`${namespace}.changeLogLoadError`));
    } finally {
      setChangeLogsLoading(false);
    }
  }, [salonId, entityType, entityId, namespace, t]);

  // Fetch change logs once when section becomes visible for this entity
  useEffect(() => {
    if (!isVisible) {
      fetchedKeyRef.current = null;
      return;
    }
    if (!entityId) return;

    const fetchKey = `${entityType}:${entityId}`;
    if (fetchedKeyRef.current === fetchKey) {
      return;
    }
    fetchedKeyRef.current = fetchKey;
    setChangeLogs([]);
    setChangeLogsError('');
    fetchChangeLogs();
  }, [isVisible, entityId, entityType, fetchChangeLogs]);

  // Scroll to change log section after load settles
  useEffect(() => {
    if (isVisible && !changeLogsLoading && changeLogSectionRef.current && scrollContainerRef?.current) {
      setTimeout(() => {
        if (changeLogSectionRef.current) {
          changeLogSectionRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          });
        }
      }, 100);
    }
  }, [isVisible, changeLogsLoading, scrollContainerRef]);

  useEffect(() => {
    if (!isVisible || changeLogs.length === 0) {
      return;
    }

    const pendingLookups = new Map();

    changeLogs.forEach(log => {
      const fieldConfig = getReferenceFieldConfig(log.field_name);
      if (!fieldConfig) {
        return;
      }

      ['previous_value', 'new_value'].forEach(valueKey => {
        const cacheKey = getReferenceCacheKey(log.field_name, log[valueKey]);
        if (!cacheKey || valueLabelCache[cacheKey]) {
          return;
        }

        const numericValue = parseInt(log[valueKey], 10);
        if (!Number.isNaN(numericValue)) {
          pendingLookups.set(cacheKey, {
            field_name: log.field_name,
            value: numericValue
          });
        }
      });
    });

    if (pendingLookups.size === 0) {
      return;
    }

    const fetchReferenceLabel = async (fieldName, id, token) => {
      const config = getReferenceFieldConfig(fieldName);
      if (!config) return null;

      let endpoint = '';
      let responseKey = '';

      if (config.resource === 'dress') {
        endpoint = `/api/salons/${salonId}/dresses/${id}`;
        responseKey = 'dress';
      } else if (config.resource === 'accessory') {
        endpoint = `/api/salons/${salonId}/accessories/${id}`;
        responseKey = 'accessory';
      } else {
        return null;
      }

      try {
        const response = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return response.data?.[responseKey]?.name || null;
      } catch (error) {
        console.warn('Unable to fetch reference label for change log entry', {
          fieldName,
          id,
          error
        });
        return null;
      }
    };

    const fetchLabels = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const responses = await Promise.all(
          Array.from(pendingLookups.entries()).map(([cacheKey, lookup]) =>
            fetchReferenceLabel(lookup.field_name, lookup.value, token).then(label => ({
              cacheKey,
              label
            }))
          )
        );

        const newLabels = responses.reduce((acc, result) => {
          if (result?.label) {
            acc[result.cacheKey] = result.label;
          }
          return acc;
        }, {});

        if (Object.keys(newLabels).length > 0) {
          setValueLabelCache(prev => ({ ...prev, ...newLabels }));
        }
      } catch (error) {
        console.error('Error fetching reference labels for change log:', error);
      }
    };

    fetchLabels();
  }, [isVisible, changeLogs, salonId, valueLabelCache]);

  // Default field name mapper if not provided
  const defaultGetLocalizedFieldName = (fieldName) => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getFieldName = getLocalizedFieldName || defaultGetLocalizedFieldName;

  const FIELD_DATE_MAP = new Set([
    'arrival_date',
    'reservation_date',
    'wedding_date',
    'contract_date',
    'dress_pickup_date',
    'dress_return_date'
  ]);

  const FIELD_DATETIME_MAP = new Set([
    'measurement_datetime',
    'dress_pickup_datetime',
    'dress_return_datetime',
    'appointment_datetime'
  ]);

  const FIELD_BOOLEAN_MAP = new Set([
    'paid_in_full'
  ]);

  const FIELD_IMAGE_MAP = new Set([
    'image',
    'display_image',
    'video_url',
    'video2_url'
  ]);

  const getCachedReferenceLabel = (fieldName, value) => {
    const cacheKey = getReferenceCacheKey(fieldName, value);
    if (!cacheKey) return null;
    return valueLabelCache[cacheKey] || null;
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return '';

    const formattedDate = formatWeddingDate(timestamp) || '';
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${formattedDate} ${hours}:${minutes}`;
  };

  const formatFieldValue = (fieldName, value) => {
    const fieldConfig = getReferenceFieldConfig(fieldName);
    if (fieldConfig) {
      const cachedLabel = getCachedReferenceLabel(fieldName, value);
      if (cachedLabel) {
        return cachedLabel;
      }
    }

    if (value === null || value === undefined || value === '') {
      return <em style={{ color: '#a0aec0' }}>{t(`${namespace}.emptyValue`)}</em>;
    }

    if (FIELD_DATETIME_MAP.has(fieldName)) {
      return formatDateTime(value) || value;
    }

    if (FIELD_DATE_MAP.has(fieldName)) {
      return formatWeddingDate(value) || value;
    }
    
    if (FIELD_IMAGE_MAP.has(fieldName)) {
      return t('common.image');
    }

    if (FIELD_BOOLEAN_MAP.has(fieldName)) {
      const isTrue = value === true || value === 1 || value === '1' || value === 'true';
      return isTrue ? t('common.yes') : t('common.no');
    }

    if (DRESS_CARE_FIELDS.has(fieldName)) {
      return formatDressCareValue(t, fieldName, value);
    }

    return value;
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div ref={changeLogSectionRef} className="form-section" style={{ marginTop: '20px', borderTop: '2px solid #e2e8f0', paddingTop: '20px' }}>
      <h4 style={{ marginBottom: '15px', color: '#2d3748' }}>{t(titleKey)}</h4>
      {changeLogsLoading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div className="loading-spinner"></div>
        </div>
      )}
      {changeLogsError && (
        <div className="error-message">{changeLogsError}</div>
      )}
      {!changeLogsLoading && !changeLogsError && changeLogs.length === 0 && (
        <div style={{ textAlign: 'center', color: '#718096', padding: '20px' }}>
          {t(`${namespace}.noChangeLogs`)}
        </div>
      )}
      {!changeLogsLoading && !changeLogsError && changeLogs.length > 0 && (
        <div style={{ overflowX: 'auto', maxHeight: '400px', overflowY: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
            backgroundColor: '#fff'
          }}>
            <thead>
              <tr style={{
                backgroundColor: '#f7fafc',
                borderBottom: '2px solid #e2e8f0',
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#2d3748',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t(`${namespace}.changeLogDate`)}
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#2d3748',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t(`${namespace}.changeLogField`)}
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#2d3748',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t(`${namespace}.changeLogPrevious`)}
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#2d3748',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t(`${namespace}.changeLogNew`)}
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: 600,
                  color: '#2d3748',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>
                  {t(`${namespace}.changeLogUser`)}
                </th>
              </tr>
            </thead>
            <tbody>
              {changeLogs.map((log, index) => (
                <tr 
                  key={log.id} 
                  style={{
                    borderBottom: '1px solid #e2e8f0',
                    backgroundColor: index % 2 === 0 ? '#fff' : '#f7fafc',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#edf2f7';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#fff' : '#f7fafc';
                  }}
                >
                  <td style={{
                    padding: '12px 16px',
                    color: '#4a5568',
                    whiteSpace: 'nowrap',
                    fontSize: '13px'
                  }}>
                    {formatDateTime(log.created_at)}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    color: '#2d3748',
                    fontWeight: 500
                  }}>
                    {getFieldName(log.field_name)}
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    color: '#e53e3e',
                    maxWidth: '200px'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      backgroundColor: '#fed7d7',
                      borderRadius: '4px',
                      fontSize: '12px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word'
                    }}>
                      {formatFieldValue(log.field_name, log.previous_value)}
                    </span>
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    color: '#38a169',
                    maxWidth: '200px'
                  }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '4px 8px',
                      backgroundColor: '#c6f6d5',
                      borderRadius: '4px',
                      fontSize: '12px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'normal',
                      overflowWrap: 'break-word'
                    }}>
                      {formatFieldValue(log.field_name, log.new_value)}
                    </span>
                  </td>
                  <td style={{
                    padding: '12px 16px',
                    color: '#4a5568',
                    fontSize: '13px'
                  }}>
                    {log.changed_by_first_name
                      ? `${log.changed_by_first_name || ''} ${log.changed_by_last_name || ''}`.trim() || log.changed_by_email
                      : <em style={{ color: '#a0aec0' }}>{t(`${namespace}.systemUser`)}</em>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ChangeLog;

